<template>
  <SoAdminPage :title="title" :back-to="backTo">
    <template #subtitle>
      <div v-if="communicationName" class="d-flex align-center ga-2">
        <SoChip :type="SoChipType.IMPORTANT" :text="EmailTemplateSender.NOVA" />
        <span>Channel: {{ communicationName }}</span>
      </div>
    </template>

    <template #headerRight>
      <div class="d-flex flex-nowrap align-center ga-2">
        <span
          v-if="versionLabel"
          class="text-medium-emphasis mr-2"
          data-testid="version-chip"
        >
          {{ versionLabel }}
        </span>
        <SoButton
          :loading="savingDraft"
          title="Save"
          large
          data-testid="save-template"
          @click="openConfirm(false)"
        />
        <SoButton
          title="Save & Publish"
          type="primary"
          large
          data-testid="publish-template"
          @click="openConfirm(true)"
        />
      </div>
    </template>

    <EmailTemplatePublishConfirmDialog
      v-model="showPublishConfirm"
      :action="confirmMakeLive ? 'publish' : 'draft'"
      :html-content="publishHtmlContent"
      :plain-content="publishPlainContent"
      :publishing="savingDraft || publishing"
      @confirm="confirmSubmit"
    />
    <EmailTemplateSyncDialog
      v-model="showSync"
      :payload="syncPayload"
      @closed="goToCreatedVersion"
    />
    <AdminEmailTemplateForm
      ref="formRef"
      @success="onSuccess"
      @version-change="versionLabel = $event"
      @communication-change="communicationName = $event"
      @submitting="onSubmitting"
    />
  </SoAdminPage>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { RouteLocationRaw } from "vue-router"
  import SoAdminPage from "@/components/templates/SoAdminPage.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoChip from "@/components/SoChip.vue"
  import { SoChipType } from "@/types/components/SoChip"
  import {
    EmailTemplateSender,
    IEmailTemplateSyncPayload,
  } from "@/models/admin/emailTemplates/emailTemplates.types"
  import AdminEmailTemplateForm from "./components/AdminEmailTemplateForm.vue"
  import EmailTemplatePublishConfirmDialog from "./components/EmailTemplatePublishConfirmDialog.vue"
  import EmailTemplateSyncDialog from "./components/EmailTemplateSyncDialog.vue"
  import { RouteNames } from "@/types/router"

  export default defineComponent({
    components: {
      SoAdminPage,
      SoButton,
      SoChip,
      AdminEmailTemplateForm,
      EmailTemplatePublishConfirmDialog,
      EmailTemplateSyncDialog,
    },
    beforeRouteLeave(): boolean {
      const form = this.$refs.formRef as InstanceType<
        typeof AdminEmailTemplateForm
      >
      if (form?.isDirty) {
        return window.confirm("You have unsaved changes. Leave without saving?")
      }
      return true
    },
    data() {
      return {
        SoChipType,
        EmailTemplateSender,
        versionLabel: "",
        communicationName: "",
        savingDraft: false,
        publishing: false,
        showPublishConfirm: false,
        confirmMakeLive: false,
        publishHtmlContent: "",
        publishPlainContent: "",
        showSync: false,
        syncPayload: null as IEmailTemplateSyncPayload | null,
        created: null as { communicationId: number; version: number } | null,
      }
    },
    computed: {
      communicationId(): number | null {
        const param = this.$route.query.communicationId
        return param ? Number(param) : null
      },
      title(): string {
        return this.$route.query.cloneFromId
          ? "Create new email template version"
          : "Create a new email template"
      },
      backTo(): RouteLocationRaw {
        if (this.communicationId) {
          return {
            name: RouteNames.AdminEmailTemplatesDetail,
            params: { communicationId: String(this.communicationId) },
          }
        }
        return { name: RouteNames.AdminEmailTemplates }
      },
    },
    methods: {
      submit(makeLive: boolean) {
        const form = this.$refs.formRef as InstanceType<
          typeof AdminEmailTemplateForm
        >
        form.handleSubmit(makeLive)
      },
      async openConfirm(makeLive: boolean) {
        const form = this.$refs.formRef as InstanceType<
          typeof AdminEmailTemplateForm
        >
        if (await form.validate()) {
          this.confirmMakeLive = makeLive
          this.publishHtmlContent = form.formData.htmlContent
          this.publishPlainContent = form.formData.plainContent
          this.showPublishConfirm = true
        }
      },
      confirmSubmit() {
        this.submit(this.confirmMakeLive)
      },
      onSubmitting(loading: boolean, makeLive: boolean) {
        this.savingDraft = loading && !makeLive
        this.publishing = loading && makeLive
        if (!loading) {
          this.showPublishConfirm = false
        }
      },
      onSuccess(
        communicationId: number,
        version: number,
        syncPayload: IEmailTemplateSyncPayload,
      ) {
        this.created = { communicationId, version }
        this.syncPayload = syncPayload
        this.showSync = true
      },
      goToCreatedVersion() {
        if (!this.created) return
        this.$router.push({
          name: RouteNames.AdminEmailTemplatesDetail,
          params: { communicationId: String(this.created.communicationId) },
          query: { version: String(this.created.version) },
        })
      },
    },
  })
</script>
